import { type JSX } from "react";
import { useFormContext } from "react-hook-form";

import { ReminderType } from "../maintenance.enums";
import { maintenanceMessages } from "../maintenance.intl";
import { type MaintenanceScheduleDraft } from "../types/maintenance.types";
import {
  getMaintenanceDurationLabel,
  getMaintenanceLeadTimeLabel,
  formatMaintenanceDateTime,
} from "../utils/maintenanceTime";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const getDateValue = (date: string | Date | undefined): Date | undefined => {
  if (!date || typeof date === "string") {
    return undefined;
  }

  return date;
};

const resolveReminderLabel = (
  draft: MaintenanceScheduleDraft,
  t: ReturnType<typeof useIntlMessages<typeof maintenanceMessages>>,
): string => {
  if (draft.reminderType === ReminderType.NONE) {
    return t("doNotNotify");
  }

  if (draft.reminderType === ReminderType.IMMEDIATE) {
    return t("notifyImmediately");
  }

  return t("customDate");
};

interface MaintenanceReviewDialogProps {
  open: boolean;
  onClose: () => void;
  onPublish: (draft: MaintenanceScheduleDraft) => void;
  isPublishing?: boolean;
}

/**
 * Renders the review and publish dialog for a maintenance schedule.
 *
 * @param props Component props.
 * @returns Maintenance review dialog element.
 */
const MaintenanceReviewDialog = (
  props: MaintenanceReviewDialogProps,
): JSX.Element => {
  const { open, onClose, onPublish, isPublishing = false } = props;

  const t = useIntlMessages(maintenanceMessages);
  const form = useFormContext<MaintenanceScheduleDraft>();
  const draft = form.watch();

  const durationLabel = getMaintenanceDurationLabel(draft);
  const leadTimeLabel = getMaintenanceLeadTimeLabel(draft);
  const startsAt = formatMaintenanceDateTime(
    getDateValue(draft.startDate),
    getDateValue(draft.startTime),
    t("noValue"),
  );
  const endsAt = formatMaintenanceDateTime(
    getDateValue(draft.endDate),
    getDateValue(draft.endTime),
    t("noValue"),
  );
  const reminderAt = formatMaintenanceDateTime(
    getDateValue(draft.reminderDate),
    getDateValue(draft.reminderTime),
    t("noValue"),
  );

  const handlePublish = (): void => {
    onPublish(draft);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-w-2xl' contentClassName='p-0'>
        <DialogHeader className='px-8 pt-8'>
          <DialogTitle className='text-center text-xl font-semibold'>
            {t("reviewAndPublish")}
          </DialogTitle>
        </DialogHeader>

        <div className='px-8 py-6'>
          <h3 className='text-lg font-semibold text-primary'>
            {t("maintenanceWindow")}
          </h3>
          <dl className='mt-4 space-y-3 text-sm'>
            <div className='flex justify-between gap-6'>
              <dt className='text-slate-600'>{t("starts")}</dt>
              <dd className='font-medium text-slate-950'>{startsAt}</dd>
            </div>
            <div className='flex justify-between gap-6'>
              <dt className='text-slate-600'>{t("ends")}</dt>
              <dd className='font-medium text-slate-950'>{endsAt}</dd>
            </div>
            {durationLabel && (
              <div className='flex justify-between gap-6'>
                <dt className='text-slate-600'>{t("duration")}</dt>
                <dd className='font-medium text-slate-950'>{durationLabel}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className='border-y border-slate-200 px-8 py-6'>
          <h3 className='text-lg font-semibold text-primary'>
            {t("reminder")}
          </h3>
          <p className='mt-4 text-sm font-semibold text-slate-950'>
            {resolveReminderLabel(draft, t)}
          </p>
          {draft.reminderType === ReminderType.CUSTOM && (
            <dl className='mt-4 space-y-3 text-sm'>
              <div className='flex justify-between gap-6'>
                <dt className='text-slate-600'>{t("starts")}</dt>
                <dd className='font-medium text-slate-950'>{reminderAt}</dd>
              </div>
              {leadTimeLabel && (
                <div className='flex justify-between gap-6'>
                  <dt className='text-slate-600'>{t("leadTime")}</dt>
                  <dd className='font-medium text-slate-950'>
                    {leadTimeLabel}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </div>

        <div className='px-8 py-6'>
          <h3 className='text-lg font-semibold text-primary'>{t("notice")}</h3>
          <p className='mt-4 rounded-md bg-slate-50 p-4 text-sm leading-6 text-slate-800'>
            {draft.notice || t("noValue")}
          </p>
        </div>

        <DialogFooter className='border-t border-slate-200 px-8 py-6 sm:justify-between'>
          <Button variant='ghost' onClick={onClose}>
            {t("edit")}
          </Button>
          <Button onClick={handlePublish} disabled={isPublishing}>
            {t("publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceReviewDialog;
